/**
 * Inscription Field Manager
 * FINAL VERSION: Force-Updates Cart Drawer HTML
 * - Fixes "Text not showing" by injecting fresh HTML from Shopify
 * - Fixes "Error updating cart" by stopping propagation
 * - Fixes "Ghost Key" by re-scanning new DOM
 */

if (typeof window.InscriptionManager === 'undefined') {

  class InscriptionManager {
    constructor() {
      this.fields = new Map(); 
      this.observer = null;
    }

    init() {
      this.rescan();

      // Observer: Watches for Cart Drawer to be injected or updated
      if (!this.observer) {
        const target = document.querySelector('#cart-drawer, cart-drawer, .cart-drawer, [id*="cart-drawer"]') || document.body;
        
        this.observer = new MutationObserver((mutations) => {
          // We only care if nodes were added (drawer opened or updated)
          const hasAddedNodes = mutations.some(m => m.addedNodes.length > 0);
          if (hasAddedNodes) {
            this.rescan();
          }
        });

        this.observer.observe(target, { childList: true, subtree: true });
        console.log('[InscriptionManager] Observer active');
      }
    }

    rescan() {
      const inputs = document.querySelectorAll('[data-inscription-field]');
      
      inputs.forEach((input) => {
        const itemKey = input.dataset.itemKey;
        // Register if we haven't mapped this specific element yet
        if (!this.fields.has(itemKey) || this.fields.get(itemKey).input !== input) {
          this.registerField(input);
        }
      });
    }

    registerField(input) {
      const itemKey = input.dataset.itemKey;
      const wrapper = input.closest('.inscription-field-wrapper');
      const statusDiv = wrapper ? wrapper.querySelector('.inscription-status') : null;

      if (!itemKey || !statusDiv) return;

      this.fields.set(itemKey, {
        input,
        statusDiv,
        abortController: null,
        isSaving: false,
        originalValue: input.value 
      });

      // STOP PROPAGATION: Prevents the "Error updating cart" red message
      const handleBlur = (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation(); 

        const currentKey = input.dataset.itemKey;
        const field = this.fields.get(currentKey);
        
        // Only save if value actually changed
        if (field && input.value !== field.originalValue) {
          this.saveInscription(currentKey);
        }
      };

      const handleEnter = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation(); 
          input.blur(); 
        }
      };

      // Clean up old listeners to avoid duplicates
      input.removeEventListener('blur', handleBlur);
      input.removeEventListener('keydown', handleEnter);
      input.addEventListener('change', (e) => { e.stopPropagation(); });

      // Attach listeners
      input.addEventListener('blur', handleBlur);
      input.addEventListener('keydown', handleEnter);
    }

    async saveInscription(itemKey) {
      const field = this.fields.get(itemKey);
      if (!field || field.isSaving) return;

      const { input, statusDiv } = field;
      field.isSaving = true;

      // 1. Wipe Global Errors immediately
      const globalErrorDiv = document.getElementById('CartDrawer-CartErrors');
      if (globalErrorDiv) {
        globalErrorDiv.textContent = '';
        globalErrorDiv.style.display = 'none';
      }

      statusDiv.className = 'inscription-status saving';
      statusDiv.textContent = 'Saving...';

      if (field.abortController) field.abortController.abort();
      field.abortController = new AbortController();

      try {
        // 2. Fetch Cart Data
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        
        // 3. Find Item (with Variant ID fallback for stale keys)
        let lineItem = cartData.items.find(item => item.key === itemKey);
        
        if (!lineItem) {
          const variantIdFromKey = itemKey.split(':')[0];
          lineItem = cartData.items.find(item => item.variant_id.toString() === variantIdFromKey);
        }

        if (!lineItem) throw new Error('Item not found');

        // 4. Prepare Properties
        let properties = {};
        if (Array.isArray(lineItem.properties)) {
          lineItem.properties.forEach(p => { properties[p[0]] = p[1]; });
        } else if (lineItem.properties) {
          properties = { ...lineItem.properties };
        }

        const cleanValue = input.value.trim();
        if (cleanValue === '') {
          delete properties['Inscription Name'];
        } else {
          properties['Inscription Name'] = cleanValue;
        }

        // 5. SAVE & REQUEST NEW HTML
        // We ask Shopify for 'sections: cart-drawer' to get the fresh HTML
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineItem.key, 
            properties: properties,
            sections: 'cart-drawer' 
          }),
          signal: field.abortController.signal
        });

        if (!response.ok) throw new Error('Update failed');
        
        const updatedCartData = await response.json();

        // 6. INJECT NEW HTML (The Fix)
        // This makes the text "Inscription Name: Dec" appear immediately
        if (updatedCartData.sections && updatedCartData.sections['cart-drawer']) {
          const cartDrawerContainer = document.getElementById('CartDrawer');
          if (cartDrawerContainer) {
            
            // Parse the new HTML string
            const parser = new DOMParser();
            const doc = parser.parseFromString(updatedCartData.sections['cart-drawer'], 'text/html');
            
            // Extract the inner content of the drawer
            const newContent = doc.getElementById('CartDrawer') 
              ? doc.getElementById('CartDrawer').innerHTML 
              : doc.body.innerHTML;

            // Replace the DOM content
            cartDrawerContainer.innerHTML = newContent;

            // NOTE: This DOM replacement triggers the MutationObserver automatically.
            // The Observer will call 'rescan()', which re-attaches listeners to the new inputs.
            // This keeps everything alive and working for the next edit.

            // Show "Saved ✓" animation on the fresh DOM
            const freshInput = cartDrawerContainer.querySelector(`[data-item-key="${itemKey}"]`);
            const freshWrapper = freshInput ? freshInput.closest('.inscription-field-wrapper') : null;
            const freshStatus = freshWrapper ? freshWrapper.querySelector('.inscription-status') : null;
            if (freshStatus) {
              freshStatus.className = 'inscription-status saved';
              freshStatus.textContent = 'Saved ✓';
              setTimeout(() => {
                if (document.body.contains(freshStatus)) {
                  freshStatus.textContent = '';
                  freshStatus.className = 'inscription-status';
                }
              }, 3000);
            }

            return;
          }
        }

        // Fallback: If Section API failed, just show "Saved" on the old input
        if (cleanValue === '') {
          statusDiv.className = 'inscription-status removed';
          statusDiv.textContent = 'Inscription removed';
          input.value = '';
        } else {
          statusDiv.className = 'inscription-status saved';
          statusDiv.textContent = 'Saved ✓';
        }
        
        field.originalValue = input.value;
        input.defaultValue = input.value;
        
        setTimeout(() => {
           if (document.body.contains(statusDiv)) {
             statusDiv.textContent = '';
             statusDiv.className = 'inscription-status';
           }
        }, 3000);

      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[InscriptionManager] Save Error:', error);
        // Only show error if the element still exists
        if (document.body.contains(statusDiv)) {
          statusDiv.className = 'inscription-status error';
          statusDiv.textContent = 'Error saving';
        }
      } finally {
        field.isSaving = false;
        field.abortController = null;
      }
    }
  }

  window.InscriptionManager = InscriptionManager;
  window.inscriptionManager = new InscriptionManager();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.inscriptionManager.init());
  } else {
    window.inscriptionManager.init();
  }
}